import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  FiCheck,
  FiChevronRight,
  FiHeart,
  FiMinus,
  FiPlus,
  FiRefreshCw,
  FiTruck,
  FiZoomIn,
} from "react-icons/fi";
import productData from "../Datas/productData";
import siteConfig from "../config/siteConfig";
import { CartContext } from "./CartContext";
import "../css/productdetails.scss";

const ProductDetail = () => {
  const { id } = useParams();
  const product = productData.find((item) => item.id === Number(id));
  const { addToCart, toggleWishlist, isWishlisted } = useContext(CartContext);
  const [selectedSize, setSelectedSize] = useState("");
  const [selectedColor, setSelectedColor] = useState("");
  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [added, setAdded] = useState(false);

  useEffect(() => {
    if (!product) return;
    setSelectedSize(product.size?.[0] || "");
    setSelectedColor(product.colors?.[0] || "");
    setSelectedImage(0);
    setQuantity(1);
    setAdded(false);
    document.title = `${product.name} | ${siteConfig.logo?.text || siteConfig.brandName}`;
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [product]);

  useEffect(
    () => () => {
      document.title = `${siteConfig.logo?.text || siteConfig.brandName} | Intentional planning essentials`;
    },
    []
  );

  const recommendations = useMemo(() => {
    if (!product) return [];
    const related = productData.filter(
      (item) => item.id !== product.id && item.category === product.category
    );
    return [...related, ...productData.filter((item) => item.id !== product.id && item.category !== product.category)].slice(0, 4);
  }, [product]);

  const money = (value) =>
    new Intl.NumberFormat(siteConfig.currency?.locale || "en-US", {
      style: "currency",
      currency: siteConfig.currency?.code || "USD",
    }).format(Number(value));

  if (!product) {
    return (
      <main className="product-not-found" id="main-content">
        <p className="eyebrow">PRODUCT NOT FOUND</p>
        <h1>This page has slipped out of the planner.</h1>
        <p>The item may have moved or is no longer part of the current collection.</p>
        <Link className="button button--dark" to="/shop">RETURN TO SHOP</Link>
      </main>
    );
  }

  const handleAddToCart = () => {
    addToCart(product.id, quantity, selectedSize, selectedColor);
    setAdded(true);
    window.setTimeout(() => setAdded(false), 3500);
  };

  return (
    <main className="product-detail" id="main-content">
      <nav className="product-breadcrumb page-width" aria-label="Breadcrumb">
        <Link to="/">Home</Link><FiChevronRight />
        <Link to="/shop">Shop</Link><FiChevronRight />
        <Link to={`/shop?category=${encodeURIComponent(product.category)}`}>{product.category}</Link>
      </nav>

      <section className="product-detail__container page-width">
        <div className="product-gallery">
          <div className="product-gallery__thumbs" aria-label="Product images">
            {product.images.map((image, index) => (
              <button
                key={image}
                type="button"
                className={index === selectedImage ? "is-active" : ""}
                onClick={() => setSelectedImage(index)}
                aria-label={`Show product image ${index + 1}`}
              >
                <img src={image} alt="" />
              </button>
            ))}
          </div>
          <figure className="product-gallery__main">
            <img src={product.images[selectedImage]} alt={product.name} />
            <span><FiZoomIn /> VIEW DETAIL</span>
          </figure>
        </div>

        <aside className="product-info">
          <p className="eyebrow">{product.category}</p>
          <h1>{product.name}</h1>
          <div className="product-info__rating">
            <span aria-label={`${product.rating} out of 5 stars`}>★★★★★</span>
            <a href="#product-details">{product.reviews} REVIEWS</a>
          </div>
          <p className="product-info__price">{money(product.price)}</p>
          <p className="product-info__lead">{product.description[0]}</p>

          <div className="product-option">
            <div><label htmlFor="product-color">COLOR</label><span>{selectedColor}</span></div>
            <div className="product-swatches">
              {product.colors.map((color, index) => (
                <button
                  key={color}
                  type="button"
                  className={selectedColor === color ? "is-active" : ""}
                  onClick={() => setSelectedColor(color)}
                  aria-label={`Select ${color}`}
                  style={{ "--swatch": ["#4a4037", "#ddd7ca", "#292929", "#a59b8c"][index % 4] }}
                />
              ))}
            </div>
            <select
              id="product-color"
              className="visually-hidden"
              value={selectedColor}
              onChange={(event) => setSelectedColor(event.target.value)}
            >
              {product.colors.map((color) => <option key={color}>{color}</option>)}
            </select>
          </div>

          <div className="product-option">
            <div><label htmlFor="product-size">SIZE / FORMAT</label><Link to="/contact">SIZE GUIDE</Link></div>
            <div className="product-size-options">
              {product.size.map((size) => (
                <button
                  key={size}
                  type="button"
                  className={selectedSize === size ? "is-active" : ""}
                  onClick={() => setSelectedSize(size)}
                >
                  {size}
                </button>
              ))}
            </div>
            <select
              id="product-size"
              className="visually-hidden"
              value={selectedSize}
              onChange={(event) => setSelectedSize(event.target.value)}
            >
              {product.size.map((size) => <option key={size}>{size}</option>)}
            </select>
          </div>

          <div className="product-purchase">
            <div className="product-quantity" aria-label="Quantity">
              <button type="button" onClick={() => setQuantity((value) => Math.max(1, value - 1))} aria-label="Decrease quantity"><FiMinus /></button>
              <span>{quantity}</span>
              <button type="button" onClick={() => setQuantity((value) => value + 1)} aria-label="Increase quantity"><FiPlus /></button>
            </div>
            <button type="button" className="product-add" onClick={handleAddToCart}>
              {added ? <><FiCheck /> ADDED TO CART</> : <>ADD TO CART · {money(Number(product.price) * quantity)}</>}
            </button>
            <button
              type="button"
              className={`product-wish ${isWishlisted(product.id) ? "is-active" : ""}`}
              onClick={() => toggleWishlist(product.id)}
              aria-label={isWishlisted(product.id) ? "Remove from wishlist" : "Add to wishlist"}
            >
              <FiHeart />
            </button>
          </div>

          {added && (
            <div className="product-added" role="status">
              Added to your cart. <Link to="/cartpage">VIEW CART →</Link>
            </div>
          )}

          <div className="product-shipping-note">
            <div><FiTruck /><span>Tracked shipping for {money(siteConfig.shipping?.standardRate ?? 9.99)}</span></div>
            <div><FiRefreshCw /><span>{siteConfig.shipping?.returnsWindowDays ?? 30}-day returns on eligible items</span></div>
          </div>

          <div className="product-accordions" id="product-details">
            <details open>
              <summary>DESCRIPTION <span>+</span></summary>
              <div>{product.description.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}</div>
            </details>
            <details>
              <summary>DETAILS & MATERIALS <span>+</span></summary>
              <div>
                <p>{product.material}</p>
                <dl>
                  {Object.entries(product.attributes || {}).map(([key, value]) => (
                    <React.Fragment key={key}><dt>{key}</dt><dd>{value}</dd></React.Fragment>
                  ))}
                </dl>
              </div>
            </details>
            <details>
              <summary>FEATURES <span>+</span></summary>
              <div><ul>{product.Features.map((feature) => <li key={feature}>{feature}</li>)}</ul></div>
            </details>
            <details>
              <summary>SHIPPING & RETURNS <span>+</span></summary>
              <div>
                <p>Orders are prepared in {siteConfig.shipping?.processingTime}. Shipping, duties, and taxes are shown at checkout.</p>
                <p><Link className="text-link" to="/shipping">SHIPPING GUIDE</Link> &nbsp; <Link className="text-link" to="/returns">RETURNS POLICY</Link></p>
              </div>
            </details>
          </div>
        </aside>
      </section>

      <section className="product-recommendations page-width">
        <div>
          <p className="eyebrow">COMPLETE YOUR SYSTEM</p>
          <h2>YOU MAY ALSO LIKE</h2>
        </div>
        <div className="product-card-grid">
          {recommendations.map((item) => (
            <article className="product-card" key={item.id}>
              <div className="product-card__media">
                <Link to={`/shopDetails/${item.id}`}><img src={item.image} alt={item.name} loading="lazy" /></Link>
                <button type="button" className={`product-card__wish ${isWishlisted(item.id) ? "is-active" : ""}`} onClick={() => toggleWishlist(item.id)} aria-label="Toggle wishlist"><FiHeart /></button>
                <Link className="product-card__quick" to={`/shopDetails/${item.id}`}>CHOOSE OPTIONS</Link>
              </div>
              <div className="product-card__details"><Link to={`/shopDetails/${item.id}`}>{item.name}</Link><span>{money(item.price)}</span><small>{item.colors.length} options available</small></div>
            </article>
          ))}
        </div>
      </section>
    </main>
  );
};

export default ProductDetail;
